import TextButton from 'LuxKit/Button/TextButton'
import Drop from 'LuxKit/Drop'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { rem } from 'polished'
import { useCallback, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { CHOOSE_DOUBLE_REWARDS_IMAGE_ID, CHOOSE_DOUBLE_REWARDS_OFFERS_ANCHOR, CHOOSE_DOUBLE_REWARDS_SLUG } from '../loyaltyChooseDoubleRewardsTakeover.constants'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyActiveChooseDoubleRewards from '../hooks/useLoyaltyActiveChooseDoubleRewards'
import useLoyaltyOpenChooseDoubleRewardsFlow from '../hooks/useLoyaltyOpenChooseDoubleRewardsFlow'
import useLoyaltyChooseDoubleRewardsRegion from '../hooks/useLoyaltyChooseDoubleRewardsRegion'
import useLoyaltyChooseDoubleRewardsTracking from '../hooks/useLoyaltyChooseDoubleRewardsTracking'

const Banner = styled(Sheet)`
  position: relative;
  overflow: hidden;
`

const BannerAction = styled(TextLink)`
  position: absolute;
  inset: 0;
  z-index: 1;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const Content = styled(Group)`
  position: relative;
  max-width: ${rem(900)};
  min-height: ${rem(120)};
  margin: auto;
  padding: ${rem(16)} ${rem(20)};

  ${mediaQueryUp.tablet} {
    max-width: ${rem(1148)};
    // Left padding scales with image width
    padding-inline-start: calc(${rem(40)} + clamp(${rem(100)}, 20vw, ${rem(236)}));
  }
`

const BannerDrop = styled(Drop)`
  width: ${rem(100)};

  ${mediaQueryUp.tablet} {
    position: absolute;
    inset-inline-start: ${rem(20)};
    top: 50%;
    // Image width scale proportionally banner size
    width: clamp(${rem(100)}, 20vw, ${rem(236)});
    transform: translateY(-50%);
  }
`

const Copy = styled(Group)`
  flex: 1;
  min-width: 0;
`

const TabletAction = styled(CSSBreakpoint)`
  margin-inline: ${rem(-5)};
`

const Chevron = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
`

function renderEmphasis(chunks: ReactNode) {
  return <em key="emphasis">{chunks}</em>
}

function LoyaltyChooseDoubleRewardsHomepageBanner() {
  const { impressionRef, track } = useLoyaltyChooseDoubleRewardsTracking('homepage_banner')
  const intl = useIntl()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const rewards = useLoyaltyActiveChooseDoubleRewards()
  const openFlow = useLoyaltyOpenChooseDoubleRewardsFlow()
  const { needsRegionSwitch, switchRegionLabel } = useLoyaltyChooseDoubleRewardsRegion()
  const onClick = useCallback(() => track('clicked'), [track])
  const onChoose = useCallback(() => {
    track('clicked')
    openFlow()
  }, [track, openFlow])
  if (!rewards) return null
  const { memberState } = rewards

  const selected = memberState?.status === 'enrolled' ? memberState.choice : undefined

  const to = `/societe/${CHOOSE_DOUBLE_REWARDS_SLUG}${selected ? `#${CHOOSE_DOUBLE_REWARDS_OFFERS_ANCHOR}` : ''}`

  const heading = intl.formatMessage({
    id: 'k5MdH6P5Gbjw8Fer6dpy',
    defaultMessage: 'Your next escape, your choice of <emphasis>double rewards</emphasis>',
    description: 'Heading for the homepage banner introducing the choice of double rewards',
  }, { emphasis: renderEmphasis })

  const pointsLabel = intl.formatMessage({
    id: 'QUFa7H9EzuQ8gdFF7P2Y',
    defaultMessage: 'Double Société Points',
    description: 'First reward option in the double rewards homepage banner',
  })

  const separator = intl.formatMessage({
    id: 'zUUerhIPjn7wja2y4u4L',
    defaultMessage: 'or',
    description: 'Separator between the two choices on the double rewards homepage banner',
  })

  const statusCreditsLabel = intl.formatMessage({
    id: 'r9RKNXC1RMNCX1Jcd4bp',
    defaultMessage: 'Double Status Credits',
    description: 'Second reward option in the double rewards homepage banner',
  })

  const chooseYourRewardCTALabel = intl.formatMessage({
    id: 'U388ZZx5Sia8xQhHlAlM',
    defaultMessage: 'Choose your reward',
    description: 'Button on the double rewards homepage banner opening the reward selection',
  })

  const browseOffersCTALabel = intl.formatMessage({
    id: '5bW0WEqNCKqO3AN-sqHo',
    defaultMessage: 'Browse offers',
    description: 'Button on the enrolled double rewards homepage banner linking to campaign offers',
  })

  const actionLabel = needsRegionSwitch ? switchRegionLabel : selected ? browseOffersCTALabel : chooseYourRewardCTALabel

  return <Banner ref={impressionRef} background="light-background">
    <BannerAction aria-label={actionLabel} to={needsRegionSwitch ? undefined : to} onClick={needsRegionSwitch ? onChoose : onClick} />
    <Content direction="horizontal" gap={20} tabletGap={16} verticalAlign="center">
      <BannerDrop id={CHOOSE_DOUBLE_REWARDS_IMAGE_ID} position="bottom-right" size={236} loading="eager" />
      <Copy direction="vertical" gap={4}>
        <HeadingTextBlock variant="heading3">
          {heading}
        </HeadingTextBlock>
        <CSSBreakpoint min="tablet">
          <Group direction="horizontal" wrap="wrap" gap={4} verticalAlign="center">
            {selected !== 'statusCredits' && <LoyaltyTierPill tier={displayTier} kind="primary" icon={null}>
              {pointsLabel}
            </LoyaltyTierPill>}
            {!selected && displayTier !== 'infinite' && <BodyText variant="small">
              {separator}
            </BodyText>}
            {selected !== 'points' && (displayTier !== 'infinite' || selected === 'statusCredits') && <LoyaltyTierPill tier={displayTier} kind="primary" icon={null}>
              {statusCreditsLabel}
            </LoyaltyTierPill>}
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <BodyText variant="small" underline>
            {actionLabel}
          </BodyText>
        </CSSBreakpoint>
      </Copy>
      <TabletAction only="tablet">
        <Chevron aria-hidden="true">
          <LineAngleRightBIcon />
        </Chevron>
      </TabletAction>
      <CSSBreakpoint min="desktop">
        <TextButton kind="primary" size="medium" shape="round" to={needsRegionSwitch ? undefined : to} onClick={needsRegionSwitch ? onChoose : onClick}>
          {actionLabel}
        </TextButton>
      </CSSBreakpoint>
    </Content>
  </Banner>
}

export default LoyaltyChooseDoubleRewardsHomepageBanner
